import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Paper,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { CloudUpload as UploadIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../services/api';

interface PatientDocumentUploadProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  pharmacyId: string;
  onSuccess?: () => void;
  billingItemId?: string;
  expectedAppointmentId?: string;
}

type DocumentType =
  | 'medical_clearance'
  | 'prescription_image'
  | 'insurance_card'
  | 'general'
  | 'billing'
  | 'delivery'
  | 'clinical';

const documentTypeLabels: Record<DocumentType, string> = {
  billing: 'Billing',
  delivery: 'Delivery',
  clinical: 'Clinical',
  general: 'General Document',
  medical_clearance: 'Medical Clearance',
  prescription_image: 'Prescription Image',
  insurance_card: 'Insurance Card',
};

const PatientDocumentUpload: React.FC<PatientDocumentUploadProps> = ({
  open,
  onClose,
  patientId,
  pharmacyId,
  onSuccess,
  billingItemId,
  expectedAppointmentId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth0();
  const [file, setFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<DocumentType>('general');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
      'application/pdf': ['.pdf'],
    },
    maxSize: 25 * 1024 * 1024, // 25MB
    multiple: false,
  });

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', documentType);
      formData.append('pharmacyId', pharmacyId);
      formData.append('uploadedBy', user?.name || user?.email || 'Unknown');

      if (title) formData.append('title', title);
      if (description) formData.append('description', description);
      if (billingItemId) formData.append('billingItemId', billingItemId);
      if (expectedAppointmentId) formData.append('expectedAppointmentId', expectedAppointmentId);

      const response = await api.post(
        `/api/patients/${patientId}/documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const progress = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            setUploadProgress(progress);
          },
        }
      );

      if (response.data?.success) {
        enqueueSnackbar('Document uploaded successfully', { variant: 'success' });
        handleClose();
        onSuccess?.();
      } else {
        throw new Error(response.data?.message || 'Upload failed');
      }
    } catch (err: any) {
      console.error('Error uploading document:', err);
      setError(err.response?.data?.message || err.message || 'Failed to upload document');
      enqueueSnackbar('Failed to upload document', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setDocumentType('general');
    setTitle('');
    setDescription('');
    setError(null);
    setUploadProgress(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Patient Document</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Dropzone */}
        <Paper
          {...getRootProps()}
          sx={{
            p: 3,
            mb: 2,
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.300',
            bgcolor: isDragActive ? 'action.hover' : 'background.paper',
            cursor: 'pointer',
            textAlign: 'center',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
        >
          <input {...getInputProps()} />
          <UploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
          {file ? (
            <Box>
              <Typography variant="body1" fontWeight="medium">
                {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="body1">
                {isDragActive
                  ? 'Drop the file here...'
                  : 'Drag and drop a file here, or click to select'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Accepts: PDF, JPEG, PNG, GIF (max 25MB)
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Document Type */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Document Type</InputLabel>
          <Select
            value={documentType}
            label="Document Type"
            onChange={(e) => setDocumentType(e.target.value as DocumentType)}
          >
            {Object.entries(documentTypeLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Title */}
        <TextField
          fullWidth
          label="Title (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Description */}
        <TextField
          fullWidth
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={2}
        />

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
              Uploading... {uploadProgress}%
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!file || uploading}
          startIcon={<UploadIcon />}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientDocumentUpload;
